import { Component, computed } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { BasketOutcome, BasketResult, BrokerService } from '../../core/services/broker.service';
import { KiteBrokerButtonBasket } from '../../core/models/broker.model';

/** One basket leg, shaped for display. */
interface LegView {
  role: 'Entry' | 'Stop loss';
  side: 'BUY' | 'SELL';
  symbol: string;
  exchange: string;
  qty: number;
  type: string;
  /** 'Price', or 'Trigger' for stop-loss legs. */
  priceLabel: string;
  price: string;
  product: string;
}

interface Step {
  label: string;
  state: 'done' | 'failed' | 'unknown';
}

interface StatusView {
  title: string;
  subtitle: string;
  chip: string;
  icon: string;
  /** Text / ring / glow colours for the status. */
  tone: string;
  ring: string;
  glow: string;
  bar: string;
  steps: Step[];
}

const PRODUCT_LABEL: Record<string, string> = {
  MIS: 'Intraday', CNC: 'Delivery', NRML: 'Normal',
};

const STATUS: Record<BasketOutcome, StatusView> = {
  placed: {
    title: 'Order sent to Zerodha',
    subtitle: 'Your basket was submitted from Kite.',
    chip: 'Submitted',
    icon: 'pi pi-check',
    tone: 'text-emerald-400', ring: 'bg-emerald-500/15 ring-emerald-400/40', glow: 'bg-emerald-400/30',
    bar: 'from-emerald-400 via-teal-400 to-sky-500',
    steps: [
      { label: 'Order created on NeoTrader', state: 'done' },
      { label: 'Confirmed on Zerodha Kite', state: 'done' },
    ],
  },
  cancelled: {
    title: 'Order cancelled',
    subtitle: 'You cancelled the basket on Kite. Nothing was placed.',
    chip: 'Cancelled',
    icon: 'pi pi-times',
    tone: 'text-rose-400', ring: 'bg-rose-500/15 ring-rose-400/40', glow: 'bg-rose-400/25',
    bar: 'from-rose-500 via-red-400 to-orange-400',
    steps: [
      { label: 'Order created on NeoTrader', state: 'done' },
      { label: 'Cancelled on Zerodha Kite', state: 'failed' },
    ],
  },
  unknown: {
    title: 'Status not confirmed',
    subtitle: "Zerodha didn't tell us how the basket ended. Check your orders in Kite.",
    chip: 'Unconfirmed',
    icon: 'pi pi-question',
    tone: 'text-amber-400', ring: 'bg-amber-500/15 ring-amber-400/40', glow: 'bg-amber-400/25',
    bar: 'from-amber-400 via-yellow-400 to-orange-400',
    steps: [
      { label: 'Order created on NeoTrader', state: 'done' },
      { label: 'Result from Zerodha Kite', state: 'unknown' },
    ],
  },
};

/**
 * Popup with the result of a Zerodha basket order (Trade button). /broker/return sets
 * BrokerService.orderResult and routes back to the page the trade was launched from;
 * this dialog, mounted on the app root, shows it there.
 */
@Component({
  selector: 'app-broker-order-status-dialog',
  standalone: true,
  imports: [DialogModule, DatePipe, NgClass],
  templateUrl: './broker-order-status-dialog.component.html',
  // Thin, dark scrollbar for the order list only.
  styles: [`
    .order-legs-scroll { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,.18) transparent; }
    .order-legs-scroll::-webkit-scrollbar { width: 6px; }
    .order-legs-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.18); border-radius: 9999px; }
    .order-legs-scroll::-webkit-scrollbar-track { background: transparent; }
  `],
})
export class BrokerOrderStatusDialogComponent {

  readonly result = computed<BasketResult | null>(() => this.brokerService.orderResult());
  readonly status = computed<StatusView>(() => STATUS[this.result()?.outcome ?? 'unknown']);
  readonly legs = computed<LegView[]>(() => (this.result()?.legs ?? []).map(l => this.toLegView(l)));

  readonly kiteOrdersUrl = 'https://kite.zerodha.com/orders';

  constructor(private brokerService: BrokerService) {}

  get visible(): boolean {
    return this.result() !== null;
  }

  set visible(open: boolean) {
    if (!open) this.close();
  }

  close(): void {
    this.brokerService.orderResult.set(null);
  }

  private toLegView(l: KiteBrokerButtonBasket): LegView {
    const isStop = l.order_type === 'SL-M' || l.order_type === 'SL';
    const price = isStop
      ? this.rupees(l.trigger_price)
      : l.order_type === 'MARKET' ? 'Market' : this.rupees(l.price);
    return {
      role: isStop ? 'Stop loss' : 'Entry',
      side: l.transaction_type === 'SELL' ? 'SELL' : 'BUY',
      symbol: l.tradingsymbol,
      exchange: l.exchange,
      qty: l.quantity,
      type: l.order_type,
      priceLabel: isStop ? 'Trigger' : 'Price',
      price,
      product: `${PRODUCT_LABEL[l.product] ?? l.product} · ${l.product}`,
    };
  }

  private rupees(v: number | null | undefined): string {
    if (v == null || !(v > 0)) return '—';
    return '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
